import { useDayzed } from 'dayzed';
import { DateRange, ValuesRef } from '../types';
import { useState, useRef, useMemo } from 'react';
import useLatestRef from 'hooks/useLatestRef';
import { useClickAway, useMedia } from 'react-use';
import { usePopper } from 'react-popper';
import theme from 'styles/theme';
import { handleDraftApply } from './handleDraftApply';
import { getHighlightedDates } from './getHighlightedDates';
import compact from 'lodash/compact';
import { getSelectionMode } from './getSelectionMode';
import {
  getContainerProps,
  getInputProps,
  getDoneProps,
  getCancelProps,
  getPopperProps,
  getMonthProps,
  getDateProps,
  handleDateSelected,
} from './utils';
import useFocusOutside from 'hooks/useFocusOutside';

export interface DateRangePickerOptions {
  startDate?: Date;
  endDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  blockedDates?: Date[];
  onChange: (update: DateRange) => any;
}

export function useDateRangePicker(props: DateRangePickerOptions) {
  const [hoverDate, setHoveredDate] = useState<Date>();
  const [isOpen, setIsOpen] = useState(false);
  const [draftDates, setDraftDates] = useState<DateRange>({ startDate: undefined, endDate: undefined });
  const container = useRef<HTMLDivElement>(null);
  const inputContainer = useRef<HTMLDivElement>(null);
  const pickerContainer = useRef<HTMLDivElement>(null);

  const isSmall = useMedia(`screen and (min-width: ${theme.breakpoints[0]})`);
  const isMedium = useMedia(`screen and (min-width: ${theme.breakpoints[1]})`);

  const monthsToDisplay = isMedium ? 2 : 1;

  const dayzedProps = useDayzed({
    minDate: props.minDate,
    maxDate: props.maxDate,
    monthsToDisplay: monthsToDisplay,
    firstDayOfWeek: 1,
    onDateSelected: (selectedDateObj) => handleDateSelected(values, selectedDateObj),
    selected: compact([draftDates.startDate, draftDates.endDate]),
  });

  const popperProps = usePopper(inputContainer.current, pickerContainer.current, {
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom-start'],
        },
      },
    ],
  });

  const values = useLatestRef<ValuesRef['current']>({
    dayzedProps,
    props,
    hoverDate,
    setHoveredDate,
    draftDates,
    setDraftDates,
    container,
    inputContainer,
    setIsOpen,
    isOpen,
    pickerContainer,
    popperProps,
    isSmall,
    highlightedDates: getHighlightedDates(draftDates, hoverDate),
    selectionMode: getSelectionMode(draftDates),
  });

  useFocusOutside({ elementRef: container, listen: isOpen }, () => handleDraftApply(values));

  useClickAway(container, () => handleDraftApply(values));

  const additionalCallbacks = useMemo(
    () => ({
      getDateProps: getDateProps.bind(null, values),
      getMonthProps: getMonthProps.bind(null, values),
      getContainerProps: getContainerProps.bind(null, values),
      getInputProps: getInputProps.bind(null, values),
      getPopperProps: getPopperProps.bind(null, values),
      getDoneProps: getDoneProps.bind(null, values),
      getCancelProps: getCancelProps.bind(null, values),
    }),
    [values],
  );

  return { setIsOpen, isOpen, monthsToDisplay, isSmall, isMedium, ...dayzedProps, ...additionalCallbacks };
}
